import { useQuery } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  AdvisorEconomicsResponse,
  AdvisorPerformanceResponse,
  ConversionBySourceResponse,
  HouseholdDepthResponse,
  MetricsQuery,
  MotorHealthConversionResponse,
  PremiumExperienceResponse,
  RetentionResponse,
} from "../../api/metrics-contracts";

// U10-REPORTS-F — the six pilot-metric aggregate reads (analytics:read @ all). Each is
// a read-only GET over the shared cohort/window inputs. NO client-side aggregation:
// every hook returns the server rollup verbatim. Empty/zero is a real response (a zero
// row), not an error; a genuine failure surfaces as a ProblemError the surface renders.

const REPORTS = ["metrics"];

/** Serialize the shared cohort/window inputs to the query string the controller reads
 *  (`cohortFrom`,`cohortTo`,`from`,`to`). Blank bounds are omitted → the read treats
 *  them as unbounded. */
function toQueryString(q: MetricsQuery): string {
  const params = new URLSearchParams();
  if (q.cohortFrom) params.set("cohortFrom", q.cohortFrom);
  if (q.cohortTo) params.set("cohortTo", q.cohortTo);
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** The cache key: the read name + the four bounds (so a filter change refetches). */
const key = (read: string, q: MetricsQuery) =>
  [
    ...REPORTS,
    read,
    q.cohortFrom ?? null,
    q.cohortTo ?? null,
    q.from ?? null,
    q.to ?? null,
  ] as const;

/** R1 — GET /v1/metrics/conversion-by-source (numerator only; CAC denominator external). */
export function useConversionBySource(q: MetricsQuery) {
  const api = useApi();
  return useQuery({
    queryKey: key("conversion-by-source", q),
    queryFn: () =>
      api.get<ConversionBySourceResponse>(`/v1/metrics/conversion-by-source${toQueryString(q)}`),
  });
}

/** R2 — GET /v1/metrics/motor-health-conversion (same-day = attach, excluded). */
export function useMotorHealthConversion(q: MetricsQuery) {
  const api = useApi();
  return useQuery({
    queryKey: key("motor-health-conversion", q),
    queryFn: () =>
      api.get<MotorHealthConversionResponse>(
        `/v1/metrics/motor-health-conversion${toQueryString(q)}`,
      ),
  });
}

/** R3 — GET /v1/metrics/household-depth (multi-life attach + depth distributions). */
export function useHouseholdDepth(q: MetricsQuery) {
  const api = useApi();
  return useQuery({
    queryKey: key("household-depth", q),
    queryFn: () =>
      api.get<HouseholdDepthResponse>(`/v1/metrics/household-depth${toQueryString(q)}`),
  });
}

/** R4 — GET /v1/metrics/retention (renewal-eligible → renewed/lapsed + rate). */
export function useRetention(q: MetricsQuery) {
  const api = useApi();
  return useQuery({
    queryKey: key("retention", q),
    queryFn: () => api.get<RetentionResponse>(`/v1/metrics/retention${toQueryString(q)}`),
  });
}

/** R5 — GET /v1/metrics/advisor-economics (revenue side; advisor cost external). */
export function useAdvisorEconomics(q: MetricsQuery) {
  const api = useApi();
  return useQuery({
    queryKey: key("advisor-economics", q),
    queryFn: () =>
      api.get<AdvisorEconomicsResponse>(`/v1/metrics/advisor-economics${toQueryString(q)}`),
  });
}

/** R6 — GET /v1/metrics/premium-experience (price/issuance/no-spam/claims/named-advisor). */
export function usePremiumExperience(q: MetricsQuery) {
  const api = useApi();
  return useQuery({
    queryKey: key("premium-experience", q),
    queryFn: () =>
      api.get<PremiumExperienceResponse>(`/v1/metrics/premium-experience${toQueryString(q)}`),
  });
}

/** Advisor-performance board — GET /v1/metrics/advisor-performance. A per-advisor
 *  SERVICE-QUALITY read (scope-aware own/team/all, enforced server-side). It reads only
 *  the measurement WINDOW (`from`/`to`) — the cohort bounds do not apply — so its cache
 *  key is the window alone (a cohort-only change never refetches it). Rendered verbatim:
 *  no client-side aggregation, no ranking, no commission. */
export function useAdvisorPerformance(q: MetricsQuery) {
  const api = useApi();
  const params = new URLSearchParams();
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  const qs = params.toString();
  return useQuery({
    queryKey: [...REPORTS, "advisor-performance", q.from ?? null, q.to ?? null] as const,
    queryFn: () =>
      api.get<AdvisorPerformanceResponse>(`/v1/metrics/advisor-performance${qs ? `?${qs}` : ""}`),
  });
}
